import React, { useState, useEffect, useCallback } from "react";
import { Link } from "gatsby";
import { Menu, X, ShoppingCart, Search } from "lucide-react";

import { BrandLogo } from "@components/brand/BrandLogo";

import useCart from "@context/useCart";
import useSearchContext from "@hooks/useSearchContext";

import categories from "@data/products/categories";

const isClient = typeof window !== "undefined";

export const HeaderSearchButton: React.FC = () => {
    const { open } = useSearchContext();
    return (
        <Link
            to="/search"
            onClick={open}
            aria-label="Search Green Unison products"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-neutral-100"
        >
            <Search className="h-5 w-5" />
            <span className="hidden sm:inline text-sm">Search</span>
        </Link>
    );
};

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { isRehydrated, totalItems } = useCart();

    useEffect(() => {
        if (isClient) {
            const handleScroll = () => {
                setIsScrolled(window.scrollY > 20);
            };

            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const MobileLink = useCallback(
        (props: any) => <Link onClick={toggleMenu} {...props} />,
        [toggleMenu],
    );

    const mainNavLinksClass =
        "rounded-full px-4 py-2 text-sm font-extrabold text-brand-forest transition hover:bg-brand-yellow/50 hover:text-brand-green";

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 border-b border-brand-forest/10 backdrop-blur-xl transition duration-300 ${
                isScrolled ? "bg-brand-cream/90 py-2 shadow-sm" : "bg-brand-cream/80 py-3"
            }`}
        >
            <div className="mx-auto px-4 container flex items-center justify-between">
                <BrandLogo subLabel="eShop" />

                {/* Desktop Navigation */}
                <nav
                    className="hidden items-center gap-1 rounded-full border border-white/80 bg-white/55 p-1 shadow-sm md:flex"
                    aria-label="Primary navigation"
                >
                    <Link to="/" className={mainNavLinksClass} aria-label="Home">
                        Home
                    </Link>
                    <div className="relative group">
                        <Link
                            to="/shop"
                            className="text-neutral-800 hover:text-primary transition-colors flex items-center"
                            aria-label="Shop All Products"
                        >
                            eShop
                        </Link>
                        <div className="absolute left-0 mt-2 w-48 bg-white rounded shadow opacity-0 invisible group-hover:opacity-100 group-hover:visible transition duration-300 z-50 py-1">
                            <div className="absolute w-full h-2 -top-2"></div>
                            {categories.map((category) => (
                                <Link
                                    key={category.id}
                                    to={`/shop/${category.id}`}
                                    className="block px-4 py-2 text-sm text-neutral-700 hover:bg-primary-50 hover:text-primary"
                                    aria-label={`Shop ${category.name}`}
                                >
                                    {category.name}
                                </Link>
                            ))}
                            <Link
                                to="/shop"
                                className="block px-4 py-2 text-sm font-medium text-primary hover:bg-primary-50 border-t border-neutral-100"
                                aria-label="Shop All Products page"
                            >
                                View All Products
                            </Link>
                        </div>
                    </div>
                    <Link to="/about" className={mainNavLinksClass} aria-label="Learn Our Story">
                        Our Story
                    </Link>
                    <a
                        href="https://blog.greenunison.com/"
                        className="rounded-full px-4 py-2 text-sm font-extrabold text-brand-forest transition hover:bg-brand-yellow/50 hover:text-brand-green"
                    >
                        Journal
                    </a>
                    <Link to="/contact" className={mainNavLinksClass} aria-label="Contact Us">
                        Contact
                    </Link>
                    <Link
                        to="/return-policy"
                        className="text-neutral-800 hover:text-primary transition-colors py-2"
                        aria-label="View our Return Policy"
                    >
                        Return Policy
                    </Link>
                </nav>

                <div className="flex items-center">
                    <HeaderSearchButton />

                    <Link
                        to="/cart"
                        className="relative p-2 text-neutral-800 hover:text-primary transition-colors"
                        aria-label="View Cart"
                    >
                        <ShoppingCart className="size-6" />
                        {isRehydrated && totalItems > 0 && (
                            <span className="absolute -top-1 -right-1 bg-secondary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {totalItems}
                            </span>
                        )}
                    </Link>

                    <button
                        onClick={toggleMenu}
                        className="ml-4 p-2 text-neutral-800 hover:text-primary transition-colors md:hidden"
                        aria-label="Menu"
                    >
                        {isMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            {isMenuOpen && (
                <div className="md:hidden bg-white border-t border-neutral-100 shadow">
                    <div className="mx-auto px-4 py-2 container">
                        <nav className="flex flex-col gap-4 py-4">
                            <MobileLink
                                to="/"
                                className="text-neutral-800 hover:text-primary transition-colors py-2 border-b border-neutral-100"
                                aria-label="Home"
                            >
                                Home
                            </MobileLink>
                            <div className="py-2 border-b border-neutral-100">
                                <span className="text-neutral-800 font-medium mb-2 block">
                                    eShop
                                </span>
                                <div className="pl-4 flex flex-col gap-2 mt-2">
                                    {categories.map((category) => (
                                        <MobileLink
                                            key={category.id}
                                            to={`/shop/${category.id}`}
                                            className="text-sm text-neutral-700 hover:text-primary"
                                            aria-label={`Shop ${category.name}`}
                                        >
                                            {category.name}
                                        </MobileLink>
                                    ))}
                                    <MobileLink
                                        to="/shop"
                                        className="text-sm font-medium text-primary mt-2"
                                        aria-label="Shop All Products"
                                    >
                                        View All Products
                                    </MobileLink>
                                </div>
                            </div>
                            <MobileLink
                                to="/about"
                                className="text-neutral-800 hover:text-primary transition-colors py-2 border-b border-neutral-100"
                                aria-label="Learn Our Story"
                            >
                                Our Story
                            </MobileLink>
                            <a
                                href="https://blog.greenunison.com/"
                                className="text-neutral-800 hover:text-brand-green transition-colors py-2 border-b border-neutral-100"
                            >
                                Journal
                            </a>
                            <MobileLink
                                to="/contact"
                                className="text-neutral-800 hover:text-primary transition-colors py-2"
                                aria-label="Contact Us"
                            >
                                Contact
                            </MobileLink>
                            <MobileLink
                                to="/return-policy"
                                className="text-neutral-800 hover:text-primary transition-colors py-2"
                                aria-label="View our Return Policy"
                            >
                                Return Policy
                            </MobileLink>
                        </nav>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
